/**

 * L20 Shop href resolver (plan §6.4) + G2 unlistable home guard.

 *

 * G19 Spec A / G4 — BAN jwt LS alone as signed-in shop authority.

 * Auth V2 — signed-in routing requires established `/me` session memory (same as

 * `isGloballySignedInChrome`). `getToken` args are retained for call-site

 * compatibility and ignored as proof.

 *

 * Wave D chrome SSOT — signed-in Oblíbené hub is `/favorites`; legacy `/account/shop`

 * redirects there (never `/{jwt}/account/shop`). Jwt home is listability / ApiClient remap only.

 * FIX 4.3 / AC-06 F1 amendment (intentional Wave D override): Oblíbené may emit

 * lastKnown `/{tenant}/{slug}` or `/{tenant}/shop?kioskId=` before that hub.

 *

 * Fail-closed for home entry: unknown listability (`null`) is not a JWT shop destination.

 * Unlistable / unknown JWT home → `/favorites` when ≥1 **listable** (`=== true`) browser

 * favourite exists, otherwise Nakupovat (`/browse`) for generic recovery/post-auth —

 * Oblíbené chrome stays `/favorites` via `resolveShopHref` (empty guest OK).

 */



import { isPlatformChromeTenant } from '../../app/platformChromeTenant.js';

import { readPendingFavoriteSpContacts } from '../account/pendingFavoriteSpContacts.js';

import {
  type CustomerFeatureFlags,
  isCustomerBrowseEnabled,
  isCustomerScanEnabled,
  resolveCustomerFeatureFlags,
} from '../../config/customerFeatureFlags.js';

const noopTokenReader = (): string | null => null;

import {
  normalizeSalesPointSlug,
  readLastKnownSalesPoint,
} from '../../shared/session/lastKnownSalesPoint.js';

import {
  isSalesPointCustomerDiscoveryListable,
  isTenantCustomerDiscoveryListable,
} from './customerDiscoveryListability.js';



export const PLATFORM_SHOPS_PATH = '/shops';

/** Stage 3 Nakupovat entry — Stage 4 discovery UI mounts here; Stage 3 is placeholder only. */
export const PLATFORM_BROWSE_PATH = '/browse';

export const PLATFORM_FAVORITES_PATH = '/favorites';

/** Platform account Purchases hub (Wave D chrome SSOT). */

export const PLATFORM_ACCOUNT_SHOP_PATH = '/account/shop';

/** Platform guest sign-in (Wave D chrome SSOT). */

export const PLATFORM_SIGN_IN_PATH = '/sign-in';

/** Organizace — public tenant directory (not the app home). */

export const PLATFORM_ORGS_PATH = '/orgs';

/** Boot flags from window-injected runtime config (non-React callers). */
export function resolveBootCustomerFeatureFlags(): CustomerFeatureFlags {
  if (typeof window !== 'undefined') {
    const partial = (
      window as { __RPAPP_CUSTOMER_CONFIG__?: { featureFlags?: Partial<CustomerFeatureFlags> } }
    ).__RPAPP_CUSTOMER_CONFIG__?.featureFlags;
    return resolveCustomerFeatureFlags(partial);
  }
  return resolveCustomerFeatureFlags();
}

/**
 * Platform hub when browse may be disabled — Nakupovat `/browse` or fallback
 * (`/favorites` when listable guest favs exist, else `/scan` when enabled, else `/account/orders`).
 */
export function resolvePlatformHubPath(
  flags: CustomerFeatureFlags,
  nowMs: number = Date.now(),
): string {
  if (isCustomerBrowseEnabled(flags)) {
    return PLATFORM_BROWSE_PATH;
  }
  if (hasGuestFavoriteShops(nowMs)) {
    return PLATFORM_FAVORITES_PATH;
  }
  if (isCustomerScanEnabled(flags)) {
    return '/scan';
  }
  return '/account/orders';
}

/** Hierarchical ← parent when Nakupovat was the browse root. */
export function resolvePlatformBrowseParentPath(nowMs: number = Date.now()): string {
  return resolvePlatformHubPath(resolveBootCustomerFeatureFlags(), nowMs);
}

/**
 * Hierarchical ← parent for platform Oblíbené (`/favorites`).
 * Browse on → Nakupovat; browse off → `/scan` when enabled (never self-loop via hub).
 */
export function resolvePlatformFavoritesParentPath(): string {
  const flags = resolveBootCustomerFeatureFlags();
  if (isCustomerBrowseEnabled(flags)) {
    return PLATFORM_BROWSE_PATH;
  }
  if (isCustomerScanEnabled(flags)) {
    return '/scan';
  }
  return '/account/orders';
}



/** `/me` session memory — same shape as `isGloballySignedInChrome`. */

export type ShopSessionAuthority = { readonly customerId: number } | null | undefined;



/**

 * G4 — signed-in shop authority = established `/me` session only.

 * BAN getToken / jwt LS sole proof (jwt-stale after cookie clear).

 */

function hasShopCredentialAuthority(session?: ShopSessionAuthority): boolean {

  return session != null;

}



export function hasSignedInShopAccess(

  getToken: () => string | null = noopTokenReader,

  session?: ShopSessionAuthority,

): boolean {

  void getToken;

  return hasShopCredentialAuthority(session);

}



/**

 * G4 — session after `/me` only (aligns with `isGloballySignedInChrome`).

 * BAN jwt LS / getToken as signed-in proof.

 */

export function hasSignedInToken(

  getToken: () => string | null = noopTokenReader,

  session?: ShopSessionAuthority,

): boolean {

  void getToken;

  return hasShopCredentialAuthority(session);

}



/**

 * Guest / incomplete-session favourites that are known discovery-listable.

 * Fail-closed for home entry: unknown listability does not count.

 */

export function hasGuestFavoriteShops(nowMs: number = Date.now()): boolean {

  return readPendingFavoriteSpContacts(nowMs).some((fav) => {

    return isTenantCustomerDiscoveryListable(fav.tenantCode) === true;

  });

}



/**

 * Platform recovery / shop-fallback when chrome has no tenant shop path.

 * Listable guest favs → `/favorites`; otherwise Nakupovat `/browse` (Stage 3).

 * Oblíbené bottom-nav / chrome always uses `resolveShopHref` → `/favorites`

 * (empty guest OK) — never this helper’s no-favs branch (no silent Oblíbené→Obchody).

 * Stage 3 platform `/` entry is also `/browse` via `PlatformHomeRedirect`.

 */

export function resolveGuestShopHomeHref(
  nowMs: number = Date.now(),
  flags?: CustomerFeatureFlags,
): string {
  if (hasGuestFavoriteShops(nowMs)) {
    return PLATFORM_FAVORITES_PATH;
  }
  const resolvedFlags = flags ?? resolveBootCustomerFeatureFlags();
  return resolvePlatformHubPath(resolvedFlags, nowMs);
}



/**

 * Canonical Shop / Oblíbené destination for L20 surfaces.

 * Always `/favorites` (guest and signed-in). Legacy index `/account/shop` redirects

 * to `/favorites` via AccountShopTenantPage; nested `/account/shop/...` may still

 * mount this machine (sales-points picker).

 */

export function resolveShopHref(

  getToken: () => string | null = noopTokenReader,

  session?: ShopSessionAuthority,

): string {

  void getToken;

  void session;

  return PLATFORM_FAVORITES_PATH;

}



/**

 * FIX 4.3 / AC-06 F1 — lastKnown SP href for a tenant-scoped reader key.

 * FORBID `__platform__` / empty (return null; never `readLastKnown*`).

 * Slug → `/{tenant}/{slug}`; else phone-first `/{tenant}/shop?kioskId={id}`.

 * Missing/invalid → null (caller falls to Wave D hub). NEVER clearStoredCart.

 */

export function resolveLastKnownSalesPointHref(

  readerTenantCode: string | null | undefined,

): string | null {

  if (

    typeof readerTenantCode !== 'string' ||

    readerTenantCode.length === 0 ||

    isPlatformChromeTenant(readerTenantCode)

  ) {

    return null;

  }

  const last = readLastKnownSalesPoint(readerTenantCode);

  if (last === null) {

    return null;

  }

  const slug = normalizeSalesPointSlug(last.salesPointSlug);

  if (slug !== null) {

    return `/${encodeURIComponent(readerTenantCode)}/${encodeURIComponent(slug)}`;

  }

  if (Number.isFinite(last.salesPointId) && last.salesPointId > 0) {

    return `/${encodeURIComponent(readerTenantCode)}/shop?kioskId=${encodeURIComponent(String(last.salesPointId))}`;

  }

  return null;

}



/**

 * Bottom-nav **Oblíbené** (`resolveShopHref`) destination — never `/shops`.

 * Obchody owns the public directory; collapsing Oblíbené onto `/shops` makes the

 * tab a no-op while the user is already browsing shops.

 *

 * FIX 4.3 / AC-06 F1 amendment + G8: when `lastKnownReaderTenantCode` is a real

 * tenant key **and** that lastKnown SP is Obchody-listable (`=== true` on the

 * sync discovery / public allowlist cache), prefer last valid SP via

 * `resolveLastKnownSalesPointHref`. Unknown / non-listable lastKnown fail-closes

 * to Wave D hub (signed-in and guest → `/favorites`) — never deep-link

 * a SP that would be hidden from gated Favorites. Guest/null-jwt platform → pass

 * null (hub-only). FORBID reader `__platform__`. NEVER clearStoredCart on miss.

 */

export function resolvePurchasesNavHref(

  getToken: () => string | null = noopTokenReader,

  session?: ShopSessionAuthority,

  /** F1 reader key — URL tenant (tenant chrome) or accountNavTenant (platform+jwt). */

  lastKnownReaderTenantCode?: string | null,

): string {

  const lastKnownHref = resolveListableLastKnownSalesPointHref(lastKnownReaderTenantCode);

  if (lastKnownHref !== null) {

    return lastKnownHref;

  }

  const resolved = resolveShopHref(getToken, session);

  if (

    resolved === PLATFORM_SHOPS_PATH ||

    resolved.startsWith(`${PLATFORM_SHOPS_PATH}/`)

  ) {

    return PLATFORM_FAVORITES_PATH;

  }

  return resolved;

}



/**

 * G2/G8 — lastKnown SP href only when Obchody-listable (`=== true`).

 * Shared SoT for Purchases nav + header cart. Fail-closed → null (Wave D hub).

 */

export function resolveListableLastKnownSalesPointHref(

  readerTenantCode: string | null | undefined,

): string | null {

  const href = resolveLastKnownSalesPointHref(readerTenantCode);

  if (

    href === null ||

    !isLastKnownPurchasesDeepLinkListable(readerTenantCode)

  ) {

    return null;

  }

  return href;

}



/**

 * G8 — Purchases / header cart may deep-link lastKnown only when that SP is proven

 * Obchody-listable. Fail-closed: unknown (`null`) or unlistable (`false`) → refuse.

 */

export function isLastKnownPurchasesDeepLinkListable(

  readerTenantCode: string | null | undefined,

): boolean {

  if (

    typeof readerTenantCode !== 'string' ||

    readerTenantCode.length === 0 ||

    isPlatformChromeTenant(readerTenantCode)

  ) {

    return false;

  }

  const last = readLastKnownSalesPoint(readerTenantCode);

  if (last === null) {

    return false;

  }

  return (

    isSalesPointCustomerDiscoveryListable({

      salesPointId: last.salesPointId,

      tenantCode: readerTenantCode,

      salesPointSlug: last.salesPointSlug,

    }) === true

  );

}



/**

 * In-account shop link: listable jwt home + `/me` session only; otherwise null

 * (callers hide the link). G4 — getToken ignored; jwt LS alone insufficient.

 * Wave D emit: `/account/shop`.

 */

export function resolveAccountShopLinkHref(

  getToken: () => string | null = noopTokenReader,

  session?: ShopSessionAuthority,

): string | null {

  void getToken;

  if (!hasShopCredentialAuthority(session)) {

    return null;

  }

  return PLATFORM_ACCOUNT_SHOP_PATH;

}



/**

 * Post-auth landing when return-target storage is empty (Wave C).

 * Never invent `/{home}/account/shop` — listable guest favs → `/favorites`,

 * else → `/browse` (Nakupovat; Stage 3). Not Obchody `/shops` — avoids silent

 * Oblíbené→Obchody bounce for empty guest. `tenantCode` retained for call-site

 * compatibility.

 */

export function resolveDefaultPostAuthLandingPath(

  tenantCode: string,

  nowMs: number = Date.now(),

  flags?: CustomerFeatureFlags,

): string {

  void tenantCode;

  if (hasGuestFavoriteShops(nowMs)) {

    return PLATFORM_FAVORITES_PATH;

  }

  const resolvedFlags = flags ?? resolveBootCustomerFeatureFlags();

  return resolvePlatformHubPath(resolvedFlags, nowMs);

}



/**

 * Bare platform account chrome (`/account` or `/account/**`).

 */

function isBareAccountPath(pathname: string): boolean {

  const segments = pathname.split('/').filter((part) => part.length > 0);

  return segments[0] === 'account';

}



/**

 * Signed-in Orders surface: bare `/account/orders*` and legacy

 * `/{tenant}/account/orders*`.

 * Session-null here must park a return target (peer logout) before sign-in.

 */

export function isAccountOrdersPath(pathname: string): boolean {

  const segments = pathname.split('/').filter((part) => part.length > 0);

  if (segments[0] === 'account' && segments[1] === 'orders') {

    return true;

  }

  if (segments.length < 3) {

    return false;

  }

  const tenant = segments[0];

  if (tenant === undefined || isPlatformChromeTenant(tenant)) {

    return false;

  }

  return segments[1] === 'account' && segments[2] === 'orders';

}



/**

 * Signed-in Purchases surface: bare `/account/shop*` and legacy

 * `/{tenant}/account/shop*`.

 * Guest / post-logout Purchases is `/favorites` only (empty Oblíbené OK) — never sign-in.

 */

export function isAccountShopPurchasesPath(pathname: string): boolean {

  const segments = pathname.split('/').filter((part) => part.length > 0);

  if (segments[0] === 'account' && segments[1] === 'shop') {

    return true;

  }

  if (segments.length < 3) {

    return false;

  }

  const tenant = segments[0];

  if (tenant === undefined || isPlatformChromeTenant(tenant)) {

    return false;

  }

  return segments[1] === 'account' && segments[2] === 'shop';

}



function isPlatformFavoritesPath(pathname: string): boolean {

  return (

    pathname === PLATFORM_FAVORITES_PATH ||

    pathname.startsWith(`${PLATFORM_FAVORITES_PATH}/`)

  );

}



/**

 * Where to land after session end / unauthenticated Purchases.

 * Unauthenticated Purchases → `/favorites`; logout on `/favorites` → stay `/favorites`

 * (empty OK). Stage 3 `/` → `/browse` (`PlatformHomeRedirect`); recovery /

 * post-auth no-favs → `/browse` (`resolveGuestShopHomeHref` /

 * `resolveDefaultPostAuthLandingPath`). Oblíbené chrome → always `/favorites`

 * via `resolveShopHref` (never silent bounce to Obchody).

 * Wave D: `/account/shop*` → guest purchases; bare `/account/*` → `/sign-in`.

 * Leftover `/{tenant}/account/**` → locked `/sign-in` (never `/{tenant}/sign-in`).

 * Favorites + Purchases stay-put run before platform chrome → `/`.

 */

export function resolvePostLogoutLandingPath(

  pathname: string,

  tenantCode: string,

): string {

  // Stay-put before platform→`/` — do not bounce empty favs through home to `/shops`.

  if (isPlatformFavoritesPath(pathname)) {

    return PLATFORM_FAVORITES_PATH;

  }

  if (isAccountShopPurchasesPath(pathname)) {

    // Guest purchases/Oblíbené SSOT (`resolvePurchasesNavHref`) — never `/shops`, never sign-in.

    return resolvePurchasesNavHref(() => null, null);

  }

  // Wave D bare account chrome (orders, privacy, …) → platform `/sign-in`.

  if (isBareAccountPath(pathname)) {

    return PLATFORM_SIGN_IN_PATH;

  }

  if (isPlatformChromeTenant(tenantCode)) {

    return '/';

  }

  const segments = pathname.split('/').filter((part) => part.length > 0);

  const pathTenant = segments[0];

  if (

    pathTenant !== undefined &&

    !isPlatformChromeTenant(pathTenant) &&

    segments[1] === 'account'

  ) {

    return PLATFORM_SIGN_IN_PATH;

  }

  return resolvePurchasesNavHref(() => null, null);

}



/** Re-export � G11 canonical store segment for Detail deep links (/shop?kioskId= upgrade). */
export { resolveCanonicalShopStoreSegment } from './productPathSegment.js';
